import { Divider, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { QuestionCircle16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { useDataTableStyles } from "@/components/data-table";
import { ScreenHelpDrawer } from "@/features/help/ScreenHelpDrawer";

const useStyles = makeStyles({
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  sheet: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  block: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  text: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  wrap: {
    "&&": {
      height: "auto",
      whiteSpace: "normal",
      verticalAlign: "top",
    },
  },
  num: {
    "&&": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
  },
});

const TERMS = [
  ["EV 획득가치", "승인 예산 × 진척", "지금까지 한 일의 예산 가치입니다."],
  ["AC 실제원가", "입력한 원가의 합", "지금까지 실제로 쓴 돈입니다."],
  ["CPI 비용성과", "EV ÷ AC", "1이면 쓴 돈과 한 일이 같습니다."],
  ["CV 비용차이", "EV − AC", "양수면 절감이고, 음수면 초과입니다."],
  ["EAC 완료예상", "승인 예산 ÷ CPI", "이 페이스로 끝날 때의 총액입니다."],
  ["ETC 잔여예상", "EAC − AC", "앞으로 더 쓸 돈입니다."],
  ["VAC 완료차이", "승인 예산 − EAC", "끝나면 예산과 얼마나 차이 나는지입니다."],
] as const;

const JUDGMENTS = [
  ["1.00 이상", "정상"],
  ["0.90 이상 1.00 미만", "주의"],
  ["0.90 미만", "위험"],
] as const;

/** 승인 예산 ₩100,000,000일 때의 두 경우. 식은 cost-evm과 같다. */
const EXAMPLES = [
  ["EV 획득가치", "₩50,000,000", "₩40,000,000"],
  ["AC 실제원가", "₩40,000,000", "₩50,000,000"],
  ["CPI 비용성과", "1.25", "0.80"],
  ["CV 비용차이", "+₩10,000,000", "−₩10,000,000"],
  ["EAC 완료예상", "₩80,000,000", "₩125,000,000"],
  ["ETC 잔여예상", "₩40,000,000", "₩75,000,000"],
  ["VAC 완료차이", "+₩20,000,000", "−₩25,000,000"],
  ["판정", "정상", "위험"],
] as const;

/** 현황 제목 옆의 비용 성과 설명. */
export function StatusHelp() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={styles.btn} aria-label="도움말" title="도움말" onClick={() => setOpen(true)}>
        <QuestionCircle16Regular />
      </button>
      <ScreenHelpDrawer open={open} title="설명" onClose={() => setOpen(false)}>
        <div className={styles.sheet}>
          <section className={styles.block}>
            <h2 className={styles.text}>개요</h2>
            <p className={styles.text}>
              획득가치(Earned Value)는 지금까지 한 일을 승인 예산으로 환산한 값입니다. 실제 원가와 견주면, 같은 일을 계획보다 적게 썼는지 더 썼는지가 나옵니다. 승인 예산은 BAC(Budget at Completion)입니다.
            </p>
          </section>
          <Divider />
          <section className={styles.block}>
            <h2 className={styles.text}>지표</h2>
            <div className={table.frame}>
              <table className={table.table} aria-label="비용 성과 지표">
                <thead>
                  <tr>
                    <th className={table.th}>칸</th>
                    <th className={table.th}>식</th>
                    <th className={table.th}>뜻</th>
                  </tr>
                </thead>
                <tbody>
                  {TERMS.map(([cell, formula, meaning]) => (
                    <tr key={cell}>
                      <td className={mergeClasses(table.td, styles.wrap)}>{cell}</td>
                      <td className={mergeClasses(table.td, styles.wrap)}>{formula}</td>
                      <td className={mergeClasses(table.td, styles.wrap)}>{meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <Divider />
          <section className={styles.block}>
            <h2 className={styles.text}>판정</h2>
            <p className={styles.text}>CPI로 나눕니다. 실제 원가가 없으면 원가 없음이고, 승인 예산이 없으면 예산 없음입니다. 획득가치나 실제 원가가 0이면 비율과 예측은 계산 없음입니다.</p>
            <div className={table.frame}>
              <table className={table.table} aria-label="CPI 판정">
                <thead>
                  <tr>
                    <th className={table.th}>CPI</th>
                    <th className={table.th}>판정</th>
                  </tr>
                </thead>
                <tbody>
                  {JUDGMENTS.map(([range, verdict]) => (
                    <tr key={range}>
                      <td className={table.td}>{range}</td>
                      <td className={table.td}>{verdict}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <Divider />
          <section className={styles.block}>
            <h2 className={styles.text}>예시</h2>
            <p className={styles.text}>승인 예산이 ₩100,000,000일 때의 두 경우입니다.</p>
            <div className={table.frame}>
              <table className={table.table} aria-label="비용 성과 예시">
                <thead>
                  <tr>
                    <th className={table.th}>칸</th>
                    <th className={mergeClasses(table.th, styles.num)}>적게 쓴 경우</th>
                    <th className={mergeClasses(table.th, styles.num)}>더 쓴 경우</th>
                  </tr>
                </thead>
                <tbody>
                  {EXAMPLES.map(([cell, under, over]) => (
                    <tr key={cell}>
                      <td className={table.td}>{cell}</td>
                      <td className={mergeClasses(table.td, styles.num)}>{under}</td>
                      <td className={mergeClasses(table.td, styles.num)}>{over}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </ScreenHelpDrawer>
    </>
  );
}
